import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import { EstimationStatus } from '@ambire-common/controllers/estimation/types'
import { getFeeSpeedIdentifier } from '@ambire-common/controllers/signAccountOp/helper'
import { FeeSpeed, SpeedCalc } from '@ambire-common/controllers/signAccountOp/signAccountOp'
import { Warning } from '@ambire-common/interfaces/signAccountOp'
import { FeePaymentOption } from '@ambire-common/libs/estimate/interfaces'
import { ZERO_ADDRESS } from '@ambire-common/services/socket/constants'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import AssetIcon from '@common/assets/svg/AssetIcon'
import FeeIcon from '@common/assets/svg/FeeIcon'
import Alert from '@common/components/Alert'
import Select, { SectionedSelect } from '@common/components/Select'
import { SelectValue } from '@common/components/Select/types'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import collapseGasTankFeeOptions from '@common/modules/sign-account-op/components/Estimation/gasTankFeeOptions'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'
import useBackgroundService from '@web/hooks/useBackgroundService'
import { FEATURES } from '@web/constants/features'

import BundlerWarning from './components/bundlerWarning'
import EstimationSkeleton from './components/EstimationSkeleton'
import PayOption from './components/PayOption'
import ServiceFee from './components/ServiceFee'
import Sponsored from './components/Sponsored'
import { NO_FEE_OPTIONS } from './consts'
import { mapFeeOptions, sortFeeOptions } from './helpers'
import getStyles from './styles'
import { Props } from './types'

const FEE_SECTION_LIST_MENU_HEADER_HEIGHT = 34

export const SPEED_TEST_IDS = {
  slow: 'option-slow',
  medium: 'option-medium',
  fast: 'option-fast',
  ape: 'option-ape'
}

const FeeSpeedLabel = ({
  speed,
  feeTokenPriceUnavailableWarning,
  payValue,
  isValue
}: {
  speed: SpeedCalc
  feeTokenPriceUnavailableWarning?: Warning
  payValue?: SelectValue
  isValue?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <View
      style={[
        flexbox.flex1,
        flexbox.directionRow,
        flexbox.alignCenter,
        flexbox.justifySpaceBetween
      ]}
      testID={SPEED_TEST_IDS[speed.type]}
    >
      <Text weight="medium" fontSize={12} style={spacings.mrMi}>
        {t(speed.type.charAt(0).toUpperCase() + speed.type.slice(1))}
      </Text>
      {!isValue && (
        <Text
          fontSize={!feeTokenPriceUnavailableWarning ? 14 : 12}
          style={spacings.mlMi}
          numberOfLines={1}
          weight={!feeTokenPriceUnavailableWarning ? 'regular' : 'medium'}
          appearance="secondaryText"
        >
          {!feeTokenPriceUnavailableWarning
            ? formatDecimals(Number(speed.amountUsd), 'value')
            : `${formatDecimals(Number(speed.amountFormatted), 'precise')} ${
                payValue?.token.symbol
              }`}
        </Text>
      )}
    </View>
  )
}

const Estimation = ({
  signAccountOpState,
  disabled,
  hasEstimation,
  isSponsored,
  sponsor,
  updateType,
  slowRequest,
  bundlerNonceDiscrepancy,
  serviceFee
}: Props) => {
  const { dispatch } = useBackgroundService()
  const { t } = useTranslation()
  const { theme, themeType } = useTheme(getStyles)
  const { minWidthSize } = useWindowSize()

  const feeTokenPriceUnavailableWarning = useMemo(() => {
    return signAccountOpState?.warnings.find((warning) => warning.id === 'feeTokenPriceUnavailable')
  }, [signAccountOpState?.warnings])

  const canCoverFee = useCallback(
    (feeOption: FeePaymentOption) => {
      if (!signAccountOpState) return false

      const identifier = getFeeSpeedIdentifier(feeOption, signAccountOpState.accountOp.accountAddr)
      const slow = signAccountOpState.feeSpeeds[identifier]?.find((speed) => speed.type === 'slow')

      return !!slow && feeOption.availableAmount >= slow.amount
    },
    [signAccountOpState]
  )

  const mapPayOptions = useCallback(
    (feeOptions: FeePaymentOption[]) => {
      if (!signAccountOpState) return []

      // Several Gas Tank entries of the same token (one per chain the user
      // topped up on) render as identical-looking rows, so fold them into one.
      const { options, gasTankBalances } = collapseGasTankFeeOptions(feeOptions, canCoverFee)

      return options.map((feeOption) =>
        mapFeeOptions(feeOption, signAccountOpState, gasTankBalances.get(feeOption))
      )
    },
    [canCoverFee, signAccountOpState]
  )

  const payOptionsPaidByUsOrGasTank = useMemo(() => {
    if (!signAccountOpState?.estimation.availableFeeOptions.length || !hasEstimation) return []

    return mapPayOptions(
      signAccountOpState.estimation.availableFeeOptions
        .filter((feeOption) => feeOption.paidBy === signAccountOpState.accountOp.accountAddr)
        .filter((feeOption) =>
          FEATURES.gasTankUiEnabled ? true : !feeOption.token.flags.onGasTank
        )
        .sort((a: FeePaymentOption, b: FeePaymentOption) =>
          sortFeeOptions(a, b, signAccountOpState)
        )
    )
  }, [hasEstimation, mapPayOptions, signAccountOpState])

  const payOptionsPaidByEOA = useMemo(() => {
    if (!signAccountOpState?.estimation.availableFeeOptions.length || !hasEstimation) return []

    return mapPayOptions(
      signAccountOpState.estimation.availableFeeOptions
        .filter((feeOption) => feeOption.paidBy !== signAccountOpState.accountOp.accountAddr)
        .filter((feeOption) =>
          FEATURES.gasTankUiEnabled ? true : !feeOption.token.flags.onGasTank
        )
        .sort((a: FeePaymentOption, b: FeePaymentOption) =>
          sortFeeOptions(a, b, signAccountOpState)
        )
    )
  }, [hasEstimation, mapPayOptions, signAccountOpState])

  const [selectedFeeOption, setSelectedFeeOption] = useState<SelectValue['value'] | null>(null)

  const payValue = useMemo(() => {
    return (
      payOptionsPaidByUsOrGasTank.find(({ value }) => value === selectedFeeOption) ||
      payOptionsPaidByEOA.find(({ value }) => value === selectedFeeOption)
    )
  }, [payOptionsPaidByUsOrGasTank, payOptionsPaidByEOA, selectedFeeOption])

  const setFeeOption = useCallback(
    (localPayValue: any, skipDispatch?: boolean) => {
      if (!signAccountOpState?.selectedFeeSpeed) return
      setSelectedFeeOption(localPayValue.value)

      if (!skipDispatch) {
        dispatch({
          type: 'CURRENT_SIGN_ACCOUNT_OP_UPDATE',
          params: {
            updateType,
            feeToken: localPayValue.token,
            paidBy: localPayValue.paidBy,
            speed: localPayValue.speedCoverage.includes(signAccountOpState.selectedFeeSpeed)
              ? signAccountOpState.selectedFeeSpeed
              : FeeSpeed.Fast
          }
        })
      }
    },
    [dispatch, signAccountOpState?.selectedFeeSpeed, updateType]
  )

  useEffect(() => {
    if (!hasEstimation || !signAccountOpState) return
    if (payValue) return

    const selectedOption = signAccountOpState.selectedOption
    // The controller may default `selectedOption` to a gas-tank fee option,
    // but when `gasTankUiEnabled` is off those options are filtered out of
    // `payOptionsPaidByUsOrGasTank`, so `payValue` never resolves and the
    // modal stays on the skeleton. Fall back to the first visible option in
    // that case so the user can pick or proceed.
    const isHidden =
      !!selectedOption && !FEATURES.gasTankUiEnabled && !!selectedOption.token.flags.onGasTank

    if (selectedOption && !isHidden) {
      const mapped = mapFeeOptions(selectedOption, signAccountOpState)
      // The controller may have defaulted to a Gas Tank entry that got folded
      // into another row. Adopting it would leave `payValue` unresolvable and
      // the modal stuck on the skeleton, so only adopt options we render, and
      // otherwise fall through and dispatch the visible one.
      const isRendered = [...payOptionsPaidByUsOrGasTank, ...payOptionsPaidByEOA].some(
        ({ value }) => value === mapped.value
      )

      if (isRendered) {
        setFeeOption(mapped, true)
        return
      }
    }

    const fallback = payOptionsPaidByUsOrGasTank[0] || payOptionsPaidByEOA[0]
    if (fallback) setFeeOption(fallback)
  }, [
    payValue,
    setFeeOption,
    hasEstimation,
    signAccountOpState,
    payOptionsPaidByUsOrGasTank,
    payOptionsPaidByEOA
  ])

  const feeSpeeds = useMemo(() => {
    if (!signAccountOpState?.selectedOption) return []

    const identifier = getFeeSpeedIdentifier(
      signAccountOpState.selectedOption,
      signAccountOpState.accountOp.accountAddr
    )

    // The fallback array covers a corner case, that I could not reproduce,
    // but theoretically is possible - fan speed with this identifier to be missing
    return signAccountOpState.feeSpeeds[identifier] || []
  }, [
    signAccountOpState?.feeSpeeds,
    signAccountOpState?.selectedOption,
    signAccountOpState?.accountOp.accountAddr
  ])

  const isGaslessTransaction = useMemo(() => {
    return (
      feeSpeeds.every((speed) => !speed.amount) &&
      !signAccountOpState?.estimation.error &&
      !signAccountOpState?.errors.length &&
      !!feeSpeeds.length
    )
  }, [feeSpeeds, signAccountOpState?.errors.length, signAccountOpState?.estimation.error])

  const feeSpeedOptions = useMemo(() => {
    return feeSpeeds.map((speed) => ({
      label: (
        <FeeSpeedLabel
          speed={speed}
          feeTokenPriceUnavailableWarning={feeTokenPriceUnavailableWarning}
          payValue={payValue}
        />
      ),
      value: speed.type,
      speed,
      disabled: speed.disabled
    }))
  }, [feeSpeeds, feeTokenPriceUnavailableWarning, payValue])

  const selectedFee = useMemo(() => {
    const selectedOption =
      feeSpeedOptions.find(({ value }) => value === signAccountOpState?.selectedFeeSpeed) ||
      feeSpeedOptions[0]

    if (!selectedOption) return null

    return {
      ...selectedOption,
      label: (
        <FeeSpeedLabel
          speed={selectedOption.speed}
          feeTokenPriceUnavailableWarning={feeTokenPriceUnavailableWarning}
          payValue={payValue}
          isValue
        />
      )
    }
  }, [
    feeSpeedOptions,
    feeTokenPriceUnavailableWarning,
    payValue,
    signAccountOpState?.selectedFeeSpeed
  ])

  const onFeeSelect = useCallback(
    ({ value }: { value: string }) => {
      if (!Object.values(FeeSpeed).includes(value as FeeSpeed)) {
        // eslint-disable-next-line no-console
        console.error('Invalid fee speed')
        return
      }

      dispatch({
        type: 'CURRENT_SIGN_ACCOUNT_OP_UPDATE',
        params: {
          updateType,
          speed: value as FeeSpeed
        }
      })
    },
    [dispatch, updateType]
  )

  const feeOptionSelectSections = useMemo(() => {
    if (!payOptionsPaidByUsOrGasTank.length && !payOptionsPaidByEOA.length)
      return [
        {
          data: [NO_FEE_OPTIONS],
          key: 'no-options'
        }
      ]

    return [
      {
        title: {
          icon: <FeeIcon color={theme.secondaryText} width={16} height={16} />,
          text: t('With fee tokens from current account')
        },
        data: payOptionsPaidByUsOrGasTank,
        key: 'account-tokens'
      },
      {
        title: {
          icon: <AssetIcon color={theme.secondaryText} width={16} height={16} />,
          text: t('With native assets of my EOA accounts')
        },
        data: payOptionsPaidByEOA,
        key: 'eoa-tokens'
      }
    ]
  }, [payOptionsPaidByEOA, payOptionsPaidByUsOrGasTank, t, theme.secondaryText])

  const nativeFeeOption = signAccountOpState?.estimation.availableFeeOptions.find(
    (feeOption) =>
      feeOption.paidBy === signAccountOpState.accountOp.accountAddr &&
      feeOption.token.address === ZERO_ADDRESS
  )

  const paidByNativeValue = useMemo(() => {
    if (!serviceFee || !signAccountOpState?.estimation.availableFeeOptions.length || !hasEstimation)
      return null

    if (!nativeFeeOption) return

    const mappedFeeOption = mapFeeOptions(nativeFeeOption, signAccountOpState)
    mappedFeeOption.label = (
      <PayOption
        amount={BigInt(serviceFee.amount)}
        amountUsd={serviceFee.amountUSD}
        feeOption={nativeFeeOption}
      />
    )
    return mappedFeeOption
  }, [hasEstimation, signAccountOpState, serviceFee, nativeFeeOption])

  const v1warning = useMemo(() => {
    return signAccountOpState?.warnings.find((w) => w.id === 'v1Acc')
  }, [signAccountOpState?.warnings])

  const renderFeeOptionSectionHeader = useCallback(
    ({ section }: any) => {
      if (section.data.length === 0 || !section.title) return null

      return (
        <View
          style={[
            flexbox.directionRow,
            flexbox.alignCenter,
            spacings.phTy,
            spacings.pvTy,
            {
              // Sticky header: match the dropdown menu's own background
              // (see menuContainer in Select/styles.ts) in both themes.
              backgroundColor:
                themeType === THEME_TYPES.DARK
                  ? theme.secondaryBackground
                  : theme.primaryBackground,
              height: FEE_SECTION_LIST_MENU_HEADER_HEIGHT
            },
            section?.key === 'eoa-tokens' && {
              borderTopWidth: 1,
              borderTopColor: theme.secondaryBorder
            }
          ]}
        >
          {section.title.icon}
          <Text
            style={minWidthSize('xl') ? spacings.mlMi : spacings.mlTy}
            fontSize={minWidthSize('xl') ? 12 : 14}
            weight="medium"
            appearance="secondaryText"
          >
            {section.title.text}
          </Text>
        </View>
      )
    },
    [minWidthSize, themeType, theme.primaryBackground, theme.secondaryBackground, theme.secondaryBorder]
  )

  if (!hasEstimation && !!slowRequest) {
    return (
      <View style={spacings.ptTy}>
        <Alert
          type="warning"
          size="sm"
          title="Estimating this transaction is taking an unexpectedly long time. We'll keep trying, but it is possible that there's an issue with this network or RPC - please change your RPC provider or contact ZKX support if this issue persists."
        />
      </View>
    )
  }

  if (signAccountOpState && signAccountOpState.estimation.status === EstimationStatus.Error) {
    return null
  }

  if (
    !signAccountOpState ||
    (!hasEstimation && signAccountOpState.estimation.estimationRetryError) ||
    !payValue
  ) {
    return (
      <EstimationSkeleton
        // Overwrite the appearance in Swap/Transfer as the background behind the skeleton is different
        // and it isn't visible in dark mode otherwise
        appearance={updateType === 'Requests' ? undefined : 'tertiaryBackground'}
      />
    )
  }

  if (isSponsored) {
    return (
      <>
        {(!serviceFee || !paidByNativeValue || !nativeFeeOption) && <Sponsored sponsor={sponsor} />}
        <ServiceFee
          serviceFee={serviceFee}
          paidByNativeValue={paidByNativeValue}
          nativeFeeOption={nativeFeeOption}
        />
      </>
    )
  }

  if (isGaslessTransaction) {
    return (
      <Alert
        type="success"
        size="md"
        text={t('No fee payment required- this is a gasless (meta) transaction.')}
        style={spacings.mbSm}
      />
    )
  }

  return (
    <>
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifySpaceBetween,
          spacings.mbMi
        ]}
      >
        <Text fontSize={20} weight="medium">
          {t('Pay fee with')}
        </Text>
        {selectedFee && (
          <Select
            value={selectedFee}
            // @ts-ignore
            setValue={onFeeSelect}
            options={feeSpeedOptions}
            selectBorderWrapperStyle={{ borderRadius: 100 }}
            selectStyle={{
              height: 32,
              borderRadius: 100,
              ...spacings.phSm,
              backgroundColor: theme.controlSurfaceBackground,
              borderWidth: 1,
              borderColor: theme.controlSurfaceBorder
            }}
            menuOptionHeight={32}
            // Display a wider menu if the fee token price is unavailable
            // as the native amount takes up more space
            menuLeftHorizontalOffset={feeTokenPriceUnavailableWarning ? 100 : 48}
            menuStyle={{ minWidth: feeTokenPriceUnavailableWarning ? 200 : 148 }}
            withSearch={false}
            containerStyle={{ ...spacings.mb0, width: 116 }}
            testID="fee-speed-select"
          />
        )}
      </View>
      <SectionedSelect
        setValue={setFeeOption}
        testID="fee-option-select"
        headerHeight={FEE_SECTION_LIST_MENU_HEADER_HEIGHT}
        sections={feeOptionSelectSections}
        renderSectionHeader={renderFeeOptionSectionHeader}
        containerStyle={spacings.mb0}
        value={payValue || NO_FEE_OPTIONS}
        disabled={
          disabled ||
          (!payOptionsPaidByUsOrGasTank.length && !payOptionsPaidByEOA.length) ||
          !signAccountOpState.selectedOption
        }
        defaultValue={payValue ?? undefined}
        selectBorderWrapperStyle={{ borderRadius: 8 }}
        selectStyle={{
          height: 64,
          backgroundColor: theme.controlSurfaceBackground,
          borderWidth: 1,
          borderColor: theme.controlSurfaceBorder,
          ...spacings.phSm
        }}
        withSearch={!!payOptionsPaidByUsOrGasTank.length || !!payOptionsPaidByEOA.length}
        stickySectionHeadersEnabled
      />
      <ServiceFee
        serviceFee={serviceFee}
        paidByNativeValue={paidByNativeValue}
        nativeFeeOption={nativeFeeOption}
      />
      <BundlerWarning
        signAccountOpState={signAccountOpState}
        bundlerNonceDiscrepancy={bundlerNonceDiscrepancy}
      />
      {v1warning && !signAccountOpState.errors.length && (
        <View
          style={[
            flexbox.directionRow,
            spacings.mt,
            flexbox.alignCenter,
            flexbox.justifySpaceBetween
          ]}
        >
          <Text fontSize={12} appearance="warningText" style={spacings.mr}>
            {t(v1warning.title)}
          </Text>
        </View>
      )}
    </>
  )
}

export default React.memo(Estimation)
